<!-- The app's AI mark: two gold stars -->
<ng-template #stars>
  <svg class="acp-stars-svg" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path class="acp-star-big" d="M10 4c.5 4.4 2.6 6.5 7 7-4.4.5-6.5 2.6-7 7-.5-4.4-2.6-6.5-7-7 4.4-.5 6.5-2.6 7-7z" fill="currentColor"/>
    <path class="acp-star-small" d="M18.5 2c.25 1.9 1.1 2.75 3 3-1.9.25-2.75 1.1-3 3-.25-1.9-1.1-2.75-3-3 1.9-.25 2.75-1.1 3-3z" fill="currentColor"/>
  </svg>
</ng-template>

<div class="acp-backdrop" *ngIf="isOpen" (click)="close()"></div>

<div class="acp-panel" [class.acp-panel--open]="isOpen">

  <!-- Header -->
  <div class="acp-header">
    <div class="acp-header-left">
      <div class="acp-avatar">
        <ng-container *ngTemplateOutlet="stars"></ng-container>
      </div>
      <div class="acp-title-wrap">
        <div class="acp-title-row">
          <span class="acp-title">Synced AI</span>
          <span class="acp-beta">BETA</span>
        </div>
        <span class="acp-org" *ngIf="organisationName" title="The chat works only in the organisation you're signed in to">
          <span class="acp-org-dot" aria-hidden="true"></span>{{ organisationName }}
        </span>
      </div>
    </div>
    <button class="acp-new acp-history-btn" [class.acp-history-btn--on]="view === 'history'"
            (click)="view === 'history' ? showChat() : showHistory()" title="Your earlier chats" aria-label="Your chats">
      <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
        <path d="M2.5 8a5.5 5.5 0 1 0 1.6-3.9M2.5 2.5v2.6h2.6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M8 5v3.2l2 1.3" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
      <span>Chats</span>
    </button>
    <button class="acp-new" *ngIf="messages.length || view === 'history'" (click)="newChat()" title="Start a new chat" aria-label="New chat">
      <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
        <path d="M8 3v10M3 8h10" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
      </svg>
      <span>New chat</span>
    </button>
    <button class="acp-close" (click)="close()" aria-label="Close">
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
        <path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
      </svg>
    </button>
  </div>

  <!-- SD-1244: the user's earlier chats in this organisation -->
  <div class="acp-history" *ngIf="view === 'history'">
    <div class="acp-history-head">
      <button type="button" class="acp-history-back" (click)="showChat()" aria-label="Back to the chat">‹ Back</button>
      <span class="acp-history-title">Your chats</span>
    </div>
    <div class="acp-history-state" *ngIf="chatsLoading">
      <span class="acp-spinner"></span> Loading your chats…
    </div>
    <div class="acp-history-state acp-history-state--error" *ngIf="!chatsLoading && chatsError">
      {{ chatsError }} <button type="button" class="acp-retry" (click)="showHistory()">Try again</button>
    </div>
    <div class="acp-history-state" *ngIf="!chatsLoading && !chatsError && !chats.length">
      No earlier chats in {{ organisationName || 'this organisation' }} yet.
    </div>
    <ng-container *ngIf="!chatsLoading && chats.length">
      <div class="acp-history-group" *ngFor="let group of chatGroups">
        <div class="acp-history-label">{{ group.label }}</div>
        <button type="button" class="acp-history-item" *ngFor="let chat of group.chats"
                [class.acp-history-item--current]="chat.sessionId === state.sessionId"
                [disabled]="openingChat === chat.sessionId" (click)="openChat(chat)">
          <span class="acp-history-item-title">{{ chat.title }}</span>
          <span class="acp-history-item-meta">
            <span class="acp-spinner" *ngIf="openingChat === chat.sessionId"></span>
            {{ chat.turnCount === 1 ? '1 message' : chat.turnCount + ' messages' }} · {{ chat.lastAt | date:(group.label === 'Today' || group.label === 'Yesterday' ? 'HH:mm' : 'd MMM') }}
          </span>
        </button>
      </div>
    </ng-container>
  </div>

  <!-- Messages -->
  <div class="acp-messages" #messagesContainer [hidden]="view === 'history'">

    <!-- An earlier chat opening, or why it couldn't -->
    <div class="acp-opening" *ngIf="openingChat">
      <span class="acp-spinner"></span>
      <span>Opening <strong>{{ openingTitle }}</strong>…</span>
    </div>
    <div class="acp-open-error" *ngIf="!openingChat && openError">
      <span>{{ openError }}</span>
      <button type="button" class="acp-retry" (click)="openError = ''; showHistory()">Back to chats</button>
    </div>

    <!-- Empty / welcome state -->
    <ng-container *ngIf="messages.length === 0 && !isLoading && !openingChat && !openError">
      <div class="acp-welcome">
        <div class="acp-welcome-icon">
          <ng-container *ngTemplateOutlet="stars"></ng-container>
        </div>
        <h3 class="acp-welcome-title">What can I help with?</h3>
        <p class="acp-welcome-sub">
          I can look things up and get work done in {{ organisationName || 'your organisation' }}, using what you have
          access to. I'll always ask before changing anything.
        </p>
      </div>

      <div class="acp-suggestions">
        <p class="acp-suggestions-label">Try asking</p>
        <div class="acp-suggestion-list">
          <button class="acp-suggestion" *ngFor="let s of suggestions" (click)="sendSuggestion(s)">
            <span class="acp-suggestion-icon" aria-hidden="true">✦</span>{{ s }}
          </button>
        </div>
      </div>
    </ng-container>

    <!-- Message thread -->
    <ng-container *ngIf="messages.length > 0">
      <div *ngFor="let msg of messages; let mi = index" class="acp-msg" [class.acp-msg--user]="msg.role === 'user'" [class.acp-msg--ai]="msg.role !== 'user'" [class.acp-msg--notice]="msg.role === 'notice'">

        <!-- Notice (organisation switch, cancelled, stopped) -->
        <div class="acp-notice" *ngIf="msg.role === 'notice'" [class.acp-notice--admin]="msg.admin">{{ msg.text }}</div>

        <!-- Tools used for this answer -->
        <!-- How the AI got there: folded to one line, opens to its steps -->
        <div class="acp-run acp-run--done" *ngIf="msg.role !== 'user' && msg.role !== 'notice' && (msg.tools?.length || msg.timings)"
             [class.acp-run--open]="msg.runOpen" [class.acp-run--failed]="failedTools(msg.tools)">
          <button type="button" class="acp-run-toggle" (click)="msg.runOpen = !msg.runOpen" [attr.aria-expanded]="!!msg.runOpen">
            <span class="acp-run-stars"><ng-container *ngTemplateOutlet="stars"></ng-container></span>
            <span class="acp-run-title">{{ runSummary(msg) }}</span>
            <span class="acp-run-chevron" aria-hidden="true">▸</span>
          </button>
          <ol class="acp-timeline" *ngIf="msg.runOpen && msg.tools?.length">
            <li *ngFor="let t of msg.tools" class="acp-tl-step" [class.acp-tl-step--failed]="t.ok === false">
              <span class="acp-tl-mark" aria-hidden="true">{{ t.ok === false ? '✕' : '✓' }}</span>
              <span class="acp-tl-body">
                <span class="acp-tl-label">{{ t.label }}</span>
                <span class="acp-tl-detail" *ngIf="t.detail">{{ t.detail }}</span>
                <span class="acp-tl-detail" *ngIf="t.ok === false">didn't work</span>
              </span>
              <span class="acp-tl-time">{{ duration(t.ms) }}</span>
            </li>
          </ol>
          <div class="acp-run-timings" *ngIf="msg.runOpen && msg.timings" title="Shown to admins only">{{ msg.timings }}</div>
        </div>

        <!-- Error or refusal -->
        <div class="acp-msg-bubble acp-error" *ngIf="msg.role === 'ai' && msg.error" [class.acp-error--refusal]="isRefusal(msg)">
          <div class="acp-error-text">
            <span class="acp-error-icon" aria-hidden="true">{{ isRefusal(msg) ? '🔒' : '!' }}</span>
            <span>{{ msg.text }}</span>
          </div>
          <div class="acp-error-hint" *ngIf="isRefusal(msg)">
            Ask an admin to change this under Settings › User management › Roles &amp; access.
          </div>
          <button type="button" class="acp-retry" *ngIf="!isRefusal(msg) && msg.error?.retryText" (click)="retry(msg)" [disabled]="isLoading">Try again</button>
        </div>

        <!-- Confirmation, kept in the thread -->
        <div class="acp-confirm" *ngIf="msg.role === 'confirm'" [class.acp-confirm--done]="msg.confirmationState !== 'pending'">
          <div class="acp-confirm-label"><span aria-hidden="true">✦</span> Needs your OK</div>
          <div class="acp-confirm-prompt">{{ msg.text }}</div>
          <div class="acp-confirm-params" *ngIf="confirmationParams(msg).length">
            <div class="acp-confirm-field" *ngFor="let p of confirmationParams(msg)">
              <span class="acp-confirm-key">{{ p.key }}</span>
              <span class="acp-confirm-value">{{ p.value }}</span>
            </div>
          </div>
          <div class="acp-confirm-actions" *ngIf="msg.confirmationState === 'pending'">
            <button type="button" class="acp-confirm-cancel" (click)="cancelConfirmation(msg)" [disabled]="isLoading">Cancel</button>
            <button type="button" class="acp-confirm-ok" (click)="confirmAction(msg)" [disabled]="isLoading">Confirm</button>
          </div>
          <div class="acp-confirm-state" *ngIf="msg.confirmationState !== 'pending'">
            {{ msg.confirmationState === 'confirmed' ? 'Confirmed' : 'Cancelled' }} · {{ msg.decidedAt | date:'HH:mm' }}
          </div>
        </div>

        <!-- User message -->
        <div class="acp-msg-bubble" *ngIf="msg.role === 'user'">
          <span>{{ msg.text }}</span>
          <span class="acp-msg-time">{{ msg.timestamp | date:'HH:mm' }}</span>
        </div>

        <!-- Plain text AI message (markdown rendered) -->
        <div class="acp-msg-bubble acp-msg-rich" *ngIf="msg.role === 'ai' && !msg.error && !msg.blocks?.length">
          <div [innerHTML]="msg.text | markdown"></div>
          <div class="acp-msg-foot">
            <button type="button" class="acp-copy" (click)="copy(msg, mi)" [attr.aria-label]="'Copy answer'">{{ copiedIndex === mi ? 'Copied' : 'Copy' }}</button>
            <span class="acp-msg-time">{{ msg.timestamp | date:'HH:mm' }}</span>
          </div>
        </div>

        <!-- AI message with content blocks -->
        <div class="acp-msg-bubble acp-msg-blocks" *ngIf="msg.role === 'ai' && msg.blocks?.length">
          <ng-container *ngFor="let block of msg.blocks">

            <!-- Financial statement (P&L, Balance Sheet) -->
            <app-chat-statement *ngIf="block.type === 'statement'" [statement]="block.statement"></app-chat-statement>

            <!-- Text block -->
            <div class="acp-block-text" *ngIf="block.type === 'text'" [innerHTML]="block.text | markdown"></div>

            <!-- Table block -->
            <div class="acp-block-table" *ngIf="block.type === 'table'">
              <table>
                <thead>
                  <tr>
                    <th *ngFor="let h of block.table.headers; let i = index" [class.acp-num]="isNumericColumn(block.table.rows, i)">{{ h }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr *ngFor="let row of block.table.rows">
                    <td *ngFor="let h of block.table.headers; let i = index" [class.acp-num]="isNumericColumn(block.table.rows, i)">{{ row[i] || '' }}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- Records block: single record → card, multiple → table -->
            <ng-container *ngIf="block.type === 'records'">

              <!-- No records to show (e.g. backend filtered all by confidence) -->
              <div class="acp-block-empty" *ngIf="!block.records?.length">
                No matching records to display.
              </div>

              <!-- Single record: rich card -->
              <div class="acp-block-records" *ngIf="block.records.length === 1">
                <div class="acp-record-card"
                     [class.acp-record-card--clickable]="recordHref(block.records[0])"
                     (click)="openRecord(block.records[0], $event)">
                  <div class="acp-record-head">
                    <div class="acp-record-title">{{ block.records[0].title }}</div>
                    <a *ngIf="recordHref(block.records[0]) as href"
                       class="acp-record-open"
                       [href]="href"
                       target="_blank"
                       rel="noopener"
                       (click)="$event.stopPropagation()"
                       title="Open in new tab">
                      <svg width="13" height="13" viewBox="0 0 16 16" fill="none">
                        <path d="M6.5 3.5H3.5v9h9v-3M9.5 2.5h4v4M13 3l-6 6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                      </svg>
                    </a>
                  </div>
                  <div class="acp-record-fields">
                    <div class="acp-record-field" *ngFor="let f of getRecordFields(block.records[0].fields)">
                      <span class="acp-record-key">{{ f.key }}</span>
                      <a *ngIf="isLink(f.value)" [href]="f.value" target="_blank" rel="noopener" class="acp-record-link" (click)="$event.stopPropagation()">{{ f.value }}</a>
                      <span *ngIf="!isLink(f.value)" class="acp-record-value">{{ f.value }}</span>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Multiple records: compact row-cards, grouped by invoice when
                   several lines share the same target. -->
              <div class="acp-record-rows" *ngIf="block.records.length > 1">
                <ng-container *ngFor="let group of groupRecordsByInvoice(block.records)">

                  <!-- Solo line: vendor + total on top, meta beneath -->
                  <div class="acp-record-row" *ngIf="group.records.length === 1"
                       [class.acp-record-row--clickable]="recordHref(group.records[0])"
                       (click)="openRecord(group.records[0], $event)">
                    <div class="acp-record-row-main">
                      <div class="acp-record-row-top">
                        <div class="acp-record-row-vendor" *ngIf="recordVendor(group.records[0]) as v">
                          {{ v }}
                        </div>
                        <div class="acp-record-row-total" *ngIf="recordTotal(group.records[0]) as t">
                          {{ t }}
                        </div>
                      </div>
                      <div class="acp-record-row-meta" *ngIf="recordRowMetaFiltered(group.records[0]).length">
                        <ng-container *ngFor="let m of recordRowMetaFiltered(group.records[0]); let last = last">
                          <span class="acp-record-row-meta-item">
                            <span class="acp-record-row-meta-key">{{ m.key }}</span>
                            <span class="acp-record-row-meta-value">{{ m.value }}</span>
                          </span>
                          <span class="acp-record-row-meta-sep" *ngIf="!last">·</span>
                        </ng-container>
                      </div>
                    </div>
                    <div class="acp-record-row-right" (click)="$event.stopPropagation()">
                      <a *ngIf="recordHref(group.records[0]) as href"
                         class="acp-record-open"
                         [href]="href"
                         target="_blank"
                         rel="noopener"
                         title="Open in new tab">
                        <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
                          <path d="M6.5 3.5H3.5v9h9v-3M9.5 2.5h4v4M13 3l-6 6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                        </svg>
                      </a>
                    </div>
                  </div>

                  <!-- Grouped: one invoice card with stacked lines -->
                  <div class="acp-record-group" *ngIf="group.records.length > 1"
                       [class.acp-record-group--clickable]="recordHref(group.records[0])"
                       (click)="openRecord(group.records[0], $event)">
                    <div class="acp-record-group-head">
                      <div class="acp-record-group-head-main">
                        <div class="acp-record-group-title">{{ groupTitle(group.records) }}</div>
                        <div class="acp-record-group-sub">
                          <span class="acp-record-group-count">{{ group.records.length }} lines</span>
                          <span class="acp-record-group-sep" *ngIf="groupDate(group.records)">·</span>
                          <span class="acp-record-group-date" *ngIf="groupDate(group.records) as d">{{ d }}</span>
                          <span class="acp-record-group-sep" *ngIf="groupTotal(group.records)">·</span>
                          <span class="acp-record-group-total" *ngIf="groupTotal(group.records) as t">Total {{ t }}</span>
                        </div>
                      </div>
                      <div class="acp-record-row-right" (click)="$event.stopPropagation()">
                        <a *ngIf="recordHref(group.records[0]) as href"
                           class="acp-record-open"
                           [href]="href"
                           target="_blank"
                           rel="noopener"
                           title="Open invoice in new tab">
                          <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
                            <path d="M6.5 3.5H3.5v9h9v-3M9.5 2.5h4v4M13 3l-6 6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                          </svg>
                        </a>
                      </div>
                    </div>
                    <ng-container *ngIf="groupLines(group.records) as lines">
                      <div class="acp-record-group-lines" *ngIf="lines.length">
                        <div class="acp-record-group-line" *ngFor="let line of lines">
                          <span class="acp-record-group-line-amount" *ngIf="line.amount">{{ line.amount }}</span>
                          <span class="acp-record-group-line-desc" *ngIf="line.description">{{ line.description }}</span>
                        </div>
                      </div>
                    </ng-container>
                  </div>

                </ng-container>
              </div>

            </ng-container>

          </ng-container>
          <div class="acp-msg-foot">
            <button type="button" class="acp-copy" (click)="copy(msg, mi)" [attr.aria-label]="'Copy answer'">{{ copiedIndex === mi ? 'Copied' : 'Copy' }}</button>
            <span class="acp-msg-time">{{ msg.timestamp | date:'HH:mm' }}</span>
          </div>
        </div>
      </div>

      <!-- The turn under way: what the AI is doing, step by step -->
      <div class="acp-msg acp-msg--ai acp-msg--working" *ngIf="isLoading">
        <div class="acp-run acp-run--live" aria-live="polite">
          <div class="acp-run-head">
            <span class="acp-run-stars acp-run-stars--live"><ng-container *ngTemplateOutlet="stars"></ng-container></span>
            <span class="acp-run-title acp-shimmer">{{ writing ? 'Writing the answer' : 'Working on it' }}…</span>
            <span class="acp-run-agent" *ngIf="agentName">{{ agentName }}</span>
            <span class="acp-run-elapsed">{{ elapsed }}</span>
          </div>
          <ol class="acp-timeline">
            <li class="acp-tl-step" [class.acp-tl-step--running]="!understood">
              <span class="acp-tl-mark" aria-hidden="true"><span class="acp-spinner" *ngIf="!understood"></span><ng-container *ngIf="understood">✓</ng-container></span>
              <span class="acp-tl-body"><span class="acp-tl-label">Understanding your request</span></span>
            </li>
            <li *ngFor="let t of liveTools" class="acp-tl-step" [class.acp-tl-step--running]="t.ok === null" [class.acp-tl-step--failed]="t.ok === false">
              <span class="acp-tl-mark" aria-hidden="true">
                <span class="acp-spinner" *ngIf="t.ok === null"></span>
                <ng-container *ngIf="t.ok !== null">{{ t.ok ? '✓' : '✕' }}</ng-container>
              </span>
              <span class="acp-tl-body">
                <span class="acp-tl-label">{{ t.label }}</span>
                <span class="acp-tl-detail" *ngIf="t.detail">{{ t.detail }}</span>
                <span class="acp-tl-detail" *ngIf="t.ok === false">didn't work</span>
              </span>
              <span class="acp-tl-time">{{ duration(t.ms) }}</span>
            </li>
            <li class="acp-tl-step acp-tl-step--running" *ngIf="writing">
              <span class="acp-tl-mark" aria-hidden="true"><span class="acp-spinner"></span></span>
              <span class="acp-tl-body"><span class="acp-tl-label">Writing the answer</span></span>
            </li>
          </ol>
        </div>

        <!-- The answer as it streams in -->
        <div class="acp-msg-bubble acp-msg-rich acp-streaming" *ngIf="streamingText">
          <div [innerHTML]="streamingText | markdown"></div>
        </div>
      </div>

    </ng-container>
  </div>

  <!-- Disclaimer -->
  <p class="acp-disclaimer" [hidden]="view === 'history'">AI responses are generated from your financial data and may not be fully accurate.</p>

  <!-- Input -->
  <div class="acp-input-row" [hidden]="view === 'history'">
    <div class="acp-input-box">
    <span class="acp-input-icon" aria-hidden="true">✦</span>
    <textarea
      class="acp-input"
      [(ngModel)]="inputText"
      [placeholder]="organisationName ? 'Ask about ' + organisationName + '…' : 'Ask AI about your finances…'"
      rows="1"
      (keydown.enter)="onEnter($event)"
      (keydown.arrowup)="recallPrevious($event)"
      (keydown.arrowdown)="recallNext($event)"
      (input)="autoResize($event)"
      #inputRef>
    </textarea>
    <button class="acp-send acp-stop" *ngIf="isLoading" (click)="stop()" title="Stop" aria-label="Stop">
      <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><rect x="1" y="1" width="10" height="10" rx="2" fill="currentColor"/></svg>
    </button>
    <button class="acp-send" *ngIf="!isLoading" [disabled]="!inputText.trim()" (click)="send()" aria-label="Send">
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
        <path d="M2 8h12M10 4l4 4-4 4" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    </button>
    </div>
  </div>

</div>
